import { useEffect, useState } from "react";
import { api } from "../../api";
import { useSubView } from "../../App";
import { Btn, Card, ProgressBar } from "../../components/ui";
import Icon from "../../components/Icon";
import { BRAND, BRAND_TINT, HERO_GRAD, CARD_GRADS } from "../../theme";

/* video area: renders an <iframe> embed or a plain URL, with a watched button */

function FileViewer({ url, name, onClose }) {
  const isPdf = /\.pdf$/i.test(name || "");
  const isPpt = /\.(pptx?|ppt)$/i.test(name || "");
  const isImage = /\.(png|jpe?g|gif|webp|svg)$/i.test(name || "");
  let src = url;
  if (isPdf || isPpt) src = url + "#toolbar=0&navpanes=0";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white shrink-0">
        <span className="text-sm font-medium truncate">{name}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white text-lg px-2">✕</button>
      </div>
      <div className="flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {isImage
          ? <div className="w-full h-full flex items-center justify-center p-4"><img src={url} alt={name} className="max-w-full max-h-full object-contain" /></div>
          : <iframe src={src} title={name} className="w-full h-full border-0" />}
      </div>
    </div>
  );
}

function toEmbedUrl(u) {
  // Normal watch links can't be iframed; convert to the embeddable form.
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return u;
}

function extractIframeSrc(html) {
  const m = html.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function VideoBox({ url }) {
  const u = (url || "").trim();
  const isFile = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(u) || u.startsWith("/api/files/");
  const embedSrc = u.startsWith("<") ? extractIframeSrc(u) : null;
  const src = embedSrc || (u && !isFile && !u.startsWith("<") ? toEmbedUrl(u) : "");
  return (
    <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center text-white/70">
      {isFile ? <video src={u} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full" />
        : src ? <iframe src={src} title="lesson" className="absolute inset-0 w-full h-full" style={{ border: 0 }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen webkitallowfullscreen="true" mozallowfullscreen="true" />
          : u.startsWith("<") ? <div className="absolute inset-0 w-full h-full [&_iframe]:!absolute [&_iframe]:!inset-0 [&_iframe]:!w-full [&_iframe]:!h-full [&_iframe]:!max-w-none [&_iframe]:!border-0 [&>div]:!h-full [&>div>div]:!h-full [&>div>div]:!pt-0" dangerouslySetInnerHTML={{ __html: u.replace(/<iframe /i, '<iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowfullscreen webkitallowfullscreen mozallowfullscreen style="width:100%;height:100%;border:0" ') }} />
            : <div className="flex flex-col items-center gap-2"><Icon name="play" size={40} /><span className="text-sm">Lesson video</span></div>}
    </div>
  );
}

function AssignmentUpload({ subId }) {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getAssignment(subId).then((a) => setAssignment(a)).catch(() => {}).finally(() => setLoading(false));
  }, [subId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadAssignment(subId, file);
      setAssignment(res);
    } catch (ex) {
      alert(ex.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) return null;

  const statusColors = {
    Pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
    Approved: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20",
    Rejected: "bg-red-50 text-red-700 ring-1 ring-red-600/20",
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon name="download" size={16} className="text-slate-500" />
        <span className="font-semibold text-slate-800 text-sm">Assignment</span>
      </div>
      {assignment ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <a href={api.fileUrl(assignment.file_url)} target="_blank" rel="noreferrer" className="text-sm underline" style={{ color: BRAND }}>{assignment.filename}</a>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusColors[assignment.status] || statusColors.Pending}`}>{assignment.status}</span>
          </div>
          {assignment.feedback && <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">{assignment.feedback}</p>}
          {assignment.status === "Rejected" && (
            <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer">
              <Icon name="download" size={13} />{uploading ? "Uploading…" : "Re-upload"}
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Upload your assignment for this lesson.</span>
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer">
            <Icon name="download" size={13} />{uploading ? "Uploading…" : "Upload file"}
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      )}
    </div>
  );
}

function Lesson({ course, sub, onChanged, go, onNext, hasNext }) {
  const [viewingFile, setViewingFile] = useState(null);
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">{sub.title}</h2>
      <VideoBox url={sub.video_url} />
      {sub.description && <p className="text-slate-600 text-sm leading-relaxed">{sub.description}</p>}

      {!sub.video_completed && (
        <Btn onClick={async () => { try { await api.completeVideo(sub.id); onChanged(); } catch (e) { console.error(e); } }}>I've watched this — continue</Btn>
      )}

      {(() => {
        const visible = (sub.materials || []).filter((mt) => {
          if (mt.type === "PPT") return false; // PPTs never shown to students
          return true;
        });
        if (visible.length === 0) return null;
        return (
          <div>
            <div className="font-semibold text-slate-800 mb-2 underline">Study material</div>
            <div className="space-y-2">
              {visible.map((mt, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <span className="text-sm text-slate-700">{mt.name} <span className="text-[11px] uppercase tracking-wide text-slate-400 ml-1">{mt.type}</span></span>
                  {mt.url
                    ? <div className="flex items-center gap-3">
                        <a href={api.fileUrl(mt.url)} target="_blank" rel="noreferrer" className="text-sm underline" style={{ color: BRAND }} title="View">View</a>
                        <a href={api.fileUrl(mt.url) + "&download=1"} className="text-slate-500 hover:text-slate-800" title="Download"><Icon name="download" size={16} /></a>
                      </div>
                    : <span className="text-slate-300" title="No file"><Icon name="download" size={16} /></span>}
                </div>
              ))}
            </div>
            {viewingFile && <FileViewer url={viewingFile.url} name={viewingFile.name} onClose={() => setViewingFile(null)} />}
          </div>
        );
      })()}

      {course.assignments_enabled && sub.video_completed && <AssignmentUpload subId={sub.id} />}

      {sub.video_completed && (
        sub.question_count > 0
          ? <Btn onClick={() => go({ type: "quiz", subId: sub.id })}>{sub.quiz_passed ? "Review assessment" : "Take the assessment"} →</Btn>
          : <div className="flex items-center gap-3"><span className="text-sm text-slate-500">No assessment for this lesson.</span>{hasNext ? <Btn onClick={onNext}>Next →</Btn> : <Btn onClick={() => go(course.final_assessment_enabled !== false ? { type: "final" } : { type: "certificate" })}>{course.final_assessment_enabled !== false ? "Go to final assessment" : "Go to certificate"} →</Btn>}</div>
      )}
    </div>
  );
}

function Quiz({ subId, sub, onChanged, onNext, hasNext, toast }) {
  const [qs, setQs] = useState(null);
  const [activeSet, setActiveSet] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.quiz(subId).then((data) => {
      if (data.questions) { setQs(data.questions); setActiveSet(data.set || ""); }
      else { setQs(data); }  // fallback for old format
    }).catch((e) => toast(e.message));
  }, [subId]);
  if (result) {
    return (
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Your score</h2>
        <div className="text-4xl font-bold mb-4" style={{ color: result.passed ? "#16a34a" : "#dc2626" }}>{result.score}%</div>
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 text-sm font-semibold text-slate-600 border-b border-slate-200 pb-2 mb-2"><div>Question</div><div>Result</div></div>
          {(() => {
            // Reorder results to match the quiz display order
            const byId = {};
            (result.detail || []).forEach((d) => { byId[d.question_id] = d; });
            return qs.map((q, i) => {
              const d = byId[q.id] || { correct: false };
              return (
                <div key={i} className="grid grid-cols-2 text-sm py-1"><div className="text-slate-600">Question {i + 1}</div><div className={d.correct ? "text-green-600" : "text-red-600"}>{d.correct ? "Correct" : "Wrong"}</div></div>
              );
            });
          })()}
        </Card>
        <div className="rounded-lg p-3 text-sm mb-4" style={{ backgroundColor: BRAND_TINT, color: BRAND }}>
          {result.passed ? "Passed! The next lesson is unlocked." : `You need ${result.pass_mark}% to pass. Reattempt to continue.`}
        </div>
        {result.passed
          ? (hasNext ? <Btn onClick={onNext}>Next →</Btn> : <Btn onClick={onChanged}>Continue</Btn>)
          : <Btn onClick={() => { setResult(null); setAnswers({}); api.quiz(subId).then((data) => { if (data.questions) { setQs(data.questions); setActiveSet(data.set || ""); } else setQs(data); }).catch((e) => toast(e.message)); }}>Reattempt</Btn>}
      </div>
    );
  }
  if (!qs) return <div className="text-slate-400 text-sm">Loading assessment…</div>;
  return (
    <div className="select-none" onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()} style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}>
      <h2 className="text-xl font-bold text-slate-900 mb-2 underline">Assessment: {sub?.title}</h2>
      <div className="text-sm text-slate-500 mb-5">
        <div className="font-semibold text-slate-700">Assessment instructions</div>
        1 mark per question, no negative marking. Score at least {sub?.pass_mark}% to pass and unlock the next lesson. Reattempt any time.
      </div>
      <div className="space-y-4">
        {qs.map((q, qi) => (
          <Card key={q.id} className="p-4">
            <div className="font-medium text-slate-800 mb-3">{qi + 1}. {q.text}</div>
            <div className="space-y-2">
              {q.options.map((o, oi) => (
                <label key={oi} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="radio" name={`q-${q.id}`} checked={answers[q.id] === oi} onChange={() => setAnswers(prev => ({ ...prev, [q.id]: oi }))} style={{ accentColor: BRAND }} />{o}
                </label>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <div className="flex justify-end mt-5">
        <Btn onClick={async () => {
          const mapped = {}; qs.forEach((q) => { if (answers[q.id] !== undefined) mapped[q.id] = answers[q.id]; });
          try { const res = await api.submitQuiz(subId, mapped); setResult(res); } catch (e) { toast(e.message); }
        }}>Submit</Btn>
      </div>
    </div>
  );
}

const FEEDBACK_QS = [
  "The online delivery of this module helped me clearly understand the key concepts.",
  "The structure and pacing of the module supported my learning effectively.",
];
function Feedback({ course, onChanged, toast }) {
  const [ratings, setRatings] = useState({});
  const [text, setText] = useState("");
  if (course.feedback_submitted) return <Card className="p-8 text-center text-slate-500"><Icon name="check" size={24} className="mx-auto mb-2 text-green-600" />Thanks — your feedback has been recorded.</Card>;
  return (
    <div>
      <h2 className="text-xl font-bold text-slate-900 mb-2 underline">Course feedback</h2>
      <p className="text-sm text-slate-500 mb-5">Rate each statement from 1 (strongly disagree) to 10 (strongly agree).</p>
      <div className="space-y-5">
        {FEEDBACK_QS.map((q, i) => (
          <div key={i}>
            <div className="text-sm text-slate-700 mb-2">{i + 1}. {q}</div>
            <div className="flex gap-1 flex-wrap">
              {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
                <button key={n} onClick={() => setRatings({ ...ratings, [i]: n })} className="w-9 h-9 rounded-lg text-sm font-medium border" style={ratings[i] === n ? { backgroundColor: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#e7e5e4", color: "#57534e" }}>{n}</button>
              ))}
            </div>
          </div>
        ))}
        <div>
          <div className="text-sm text-slate-700 mb-2">Suggestions, challenges, or areas for improvement <span className="text-red-500">*</span></div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200" />
        </div>
        <Btn onClick={async () => { if (!text.trim()) return toast("Please add a suggestion before submitting."); await api.submitFeedback({ ratings, suggestions: text }); toast("Feedback submitted"); onChanged(); }}>Submit</Btn>
      </div>
    </div>
  );
}

function FinalExam({ course, toast, onDone }) {
  const [qs, setQs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.finalQuiz().then(setQs).catch((e) => toast(e.message));
  }, []);
  if (result) {
    return (
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Theory exam — your score</h2>
        <div className="text-4xl font-bold mb-4" style={{ color: result.passed ? "#16a34a" : "#dc2626" }}>{result.score}%</div>
        <div className="rounded-lg p-3 text-sm mb-4" style={{ backgroundColor: BRAND_TINT, color: BRAND }}>
          {result.passed ? "Passed! Your theory exam is complete." : `You need ${result.pass_mark}% to pass. Reattempt any time.`}
        </div>
        {result.passed
          ? <Btn onClick={onDone}>Back to final assessment</Btn>
          : <Btn onClick={() => { setResult(null); setAnswers({}); }}>Reattempt</Btn>}
      </div>
    );
  }
  if (!qs) return <div className="text-slate-400 text-sm">Loading exam…</div>;
  return (
    <div className="select-none" onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()} style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}>
      <h2 className="text-xl font-bold text-slate-900 mb-2 underline">Theory exam</h2>
      <div className="text-sm text-slate-500 mb-5">1 mark per question, no negative marking. Score at least {course.final_pass_mark}% to pass.</div>
      <div className="space-y-4">
        {qs.map((q, qi) => (
          <Card key={q.id} className="p-4">
            <div className="font-medium text-slate-800 mb-3">{qi + 1}. {q.text}</div>
            <div className="space-y-2">
              {q.options.map((o, oi) => (
                <label key={oi} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="radio" name={`fq-${q.id}`} checked={answers[q.id] === oi} onChange={() => setAnswers(prev => ({ ...prev, [q.id]: oi }))} style={{ accentColor: BRAND }} />{o}
                </label>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <div className="flex justify-end mt-5">
        <Btn onClick={async () => {
          const mapped = {}; qs.forEach((q) => { if (answers[q.id] !== undefined) mapped[q.id] = answers[q.id]; });
          try { const res = await api.submitFinalQuiz(mapped); setResult(res); } catch (e) { toast(e.message); }
        }}>Submit exam</Btn>
      </div>
    </div>
  );
}

function Final({ course, onChanged, toast }) {
  const [text, setText] = useState("");
  const [examMode, setExamMode] = useState(false);
  const ready = course.progress >= 100;
  const st = course.final_status;
  const projectOn = course.project_enabled !== false;
  const hasTheory = (course.final_questions_count || 0) > 0;
  const theoryPassed = course.final_theory_passed;
  const canSubmit = ready && projectOn && (st === "" || st === "Rejected");

  if (examMode) return <FinalExam course={course} toast={toast} onDone={() => { setExamMode(false); onChanged(); }} />;

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-slate-900 underline">Final assessment</h2>

      <Card className="p-4">
        <div className="font-semibold text-slate-800">Theory exam</div>
        {hasTheory ? (<>
          {theoryPassed
            ? <div className="mt-2 rounded-lg bg-green-50 text-green-700 text-sm px-3 py-2 flex items-center gap-2"><Icon name="check" size={15} />Passed{course.final_theory_score ? ` — ${course.final_theory_score}%` : ""}.</div>
            : <p className="text-sm text-slate-500 mt-1">Answer the MCQ exam. Score at least {course.final_pass_mark}% to pass.</p>}
          {ready
            ? <div className="mt-3"><Btn onClick={() => setExamMode(true)}>{theoryPassed ? "Retake exam" : "Attempt theory exam"} →</Btn></div>
            : <p className="text-sm text-slate-400 mt-2">Finish all modules to unlock the theory exam.</p>}
        </>) : (<>
          <p className="text-sm text-slate-500 mt-1">Completed by passing every module assessment.</p>
          <div className="mt-3"><ProgressBar value={course.progress} /><div className="text-xs text-slate-500 mt-1">{course.progress}% of modules passed</div></div>
        </>)}
      </Card>

      {projectOn && (<>
        {course.practical_video_url && (
          <Card className="p-4">
            <div className="font-semibold text-slate-800 mb-2">How to complete your practical</div>
            <VideoBox url={course.practical_video_url} />
          </Card>
        )}
        <Card className="p-4">
          <div className="font-semibold text-slate-800">Practical submission</div>
          {st === "Approved" && <div className="mt-2 rounded-lg bg-green-50 text-green-700 text-sm px-3 py-2 flex items-center gap-2"><Icon name="check" size={15} />Approved by the trainer.</div>}
          {st === "Pending" && <div className="mt-2 rounded-lg bg-amber-50 text-amber-700 text-sm px-3 py-2">Submitted — under review by the trainer.</div>}
          {st === "Rejected" && <div className="mt-2 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">Rejected. {course.final_feedback ? `Trainer note: ${course.final_feedback}` : "Please revise and re-upload."}</div>}
          {course.final_submission && <a href={api.fileUrl(course.final_submission)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-sm underline" style={{ color: BRAND }}>View current submission</a>}
          {canSubmit && (<>
            <p className="text-sm text-slate-500 mt-3 mb-2">{st === "Rejected" ? "Upload your revised file, or paste a link." : "Upload your practical file, or paste a link."}</p>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="https://… (or upload a file below)" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200" />
            <div className="flex items-center gap-3 mt-3">
              <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                <Icon name="download" size={15} />Upload file
                <input type="file" className="hidden" onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { await api.uploadFinal(f); toast("Practical file submitted"); onChanged(); } catch (ex) { toast(ex.message); } e.target.value = ""; }} />
              </label>
              <Btn className="ml-auto" disabled={!text} onClick={async () => { await api.submitFinal({ submission: text }); toast("Final assessment submitted"); onChanged(); }}>Submit link</Btn>
            </div>
          </>)}
          {!ready && <p className="text-sm text-slate-400 mt-2">Finish all modules to unlock the practical.</p>}
        </Card>
      </>)}
    </div>
  );
}

function Certificate({ course, onChanged, toast }) {
  const [applyOpen, setApplyOpen] = useState(false);
  const issued = course.certificate_issued;
  const requested = course.certificate_requested;

  if (!course.certificate_available && !issued)
    return <Card className="p-8 text-center text-slate-500"><Icon name="lock" size={22} className="mx-auto mb-2" />Complete all modules{course.project_enabled !== false ? ", pass the theory exam and get your practical approved" : " and pass the theory exam"} to unlock your certificate.</Card>;

  const print = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Certificate</title></head><body style="font-family:Georgia,serif;text-align:center;padding:80px;">
      <div style="border:8px solid #4F46E5;padding:60px;">
        <div style="letter-spacing:4px;color:#4F46E5;">CERTIFICATE OF COMPLETION</div>
        <h1 style="font-size:40px;margin:24px 0;">${course.student_name}</h1>
        <p>has successfully completed</p>
        <h2 style="color:#4F46E5;">${course.course_name}</h2>
        <p style="margin-top:40px;color:#666;">Edestia · ${new Date().toLocaleDateString()}</p>
      </div></body></html>`);
    w.print();
  };

  return (
    <div>
      {applyOpen && <ApplyAdditional onClose={() => setApplyOpen(false)} toast={toast} />}
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Certificate</h2>
      <Card className="p-5 mb-4">
        {issued ? (<>
          <div className="rounded-lg bg-green-50 text-green-700 text-sm px-3 py-2 flex items-center gap-2 mb-3"><Icon name="check" size={15} />Approved — your certificate is ready.</div>
          <p className="text-slate-600">Congratulations, {course.student_name}! You have successfully completed the course.</p>
          <Btn className="mt-4" onClick={print}><Icon name="download" size={16} />Download certificate</Btn>
        </>) : requested ? (<>
          <div className="rounded-lg bg-amber-50 text-amber-700 text-sm px-3 py-2 flex items-center gap-2"><Icon name="clock" size={15} />Your certificate request is awaiting approval. You'll be able to download it once approved.</div>
        </>) : (<>
          <p className="text-slate-600 mb-3">You've met all the requirements. Request your certificate — an admin will review and approve it, then you can download it.</p>
          <Btn onClick={async () => { try { await api.requestCertificate(); toast("Certificate requested — awaiting approval"); onChanged(); } catch (e) { toast(e.message); } }}>Request Certificate</Btn>
        </>)}
      </Card>
      <Card className="p-5"><Btn variant="outline" onClick={() => setApplyOpen(true)}>Apply for Additional Certificate</Btn></Card>
    </div>
  );
}

function ApplyAdditional({ onClose, toast }) {
  const [f, setF] = useState({ name: "", phone: "", note: "" });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <Card className="w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-semibold text-slate-900">Apply for Additional Certificate</h2><button onClick={onClose} className="text-slate-400"><Icon name="x" size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={3} placeholder="Anything you'd like us to know" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={async () => { try { await api.applyCertificate(f); toast("Request submitted — our team will contact you."); onClose(); } catch (e) { toast(e.message); } }}>Submit</Btn></div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Our team will reach out to discuss the certification process.</p>
      </Card>
    </div>
  );
}

function Intro({ course }) {
  return (
    <div>
      {course.paused && (
        <div className="rounded-lg px-4 py-3 text-sm flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-100">
          <Icon name="lock" size={15} />
          Your course is currently paused. Please contact your coordinator for access.
        </div>
      )}
      {!course.paused && course.lock_unlock_date && (
        <div className="rounded-lg px-4 py-3 text-sm flex items-center gap-2" style={{ backgroundColor: BRAND_TINT, color: BRAND }}>
          <Icon name="lock" size={15} />
          Part of this course unlocks on {new Date(course.lock_unlock_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}.
        </div>
      )}
      <div className="rounded-xl h-44 flex items-end px-6 pb-5 text-white text-2xl font-bold mb-6 bg-cover bg-center relative overflow-hidden" style={course.thumbnail_url ? { backgroundImage: `url(${api.fileUrl(course.thumbnail_url)})` } : { backgroundImage: HERO_GRAD }}>
        {course.thumbnail_url && <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />}
        <span className="relative z-10">{course.course_name}</span>
      </div>
      <Card className="p-5 mb-4"><div className="text-sm font-medium text-slate-700 mb-2">Overall progress</div><ProgressBar value={course.progress} tall /><div className="text-xs text-slate-500 mt-2">{course.modules.reduce((a, m) => a + (m.completed_count || 0), 0)} of {course.modules.reduce((a, m) => a + (m.sub_count || 0), 0)} lessons · {course.progress}% complete</div></Card>
      {course.overview && (<div className="mb-4"><h3 className="text-lg font-semibold text-slate-900 mb-2">Course overview</h3><div className="rt text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: course.overview }} /></div>)}
      {course.certifications && (<div className="mb-4"><h3 className="text-lg font-semibold text-slate-900 mb-2">Certifications</h3><div className="rt text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: course.certifications }} /></div>)}
      {course.general_instructions && (<div><h3 className="text-lg font-semibold text-slate-900 mb-2">Instructions</h3><div className="rt text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: course.general_instructions }} /></div>)}
    </div>
  );
}

function LiveClasses({ course, toast }) {
  const req = async (milestone) => {
    const date = prompt("Preferred date (YYYY-MM-DD)?");
    if (!date) return;
    try { await api.requestSession({ milestone, requested_date: date }); toast("Session requested — you'll get the date & link by email"); }
    catch (e) { toast(e.message); }
  };
  return (
    <div>
      <h2 className="text-xl font-bold text-slate-900 mb-4">Live classes</h2>
      <div className="flex gap-2 mb-6">
        <Btn disabled={!course.session_50_unlocked} onClick={() => req(50)}>{course.session_50_unlocked ? "Book first session (50%)" : "Reach 50% to book"}</Btn>
        <Btn variant="outline" disabled={!course.session_80_unlocked} onClick={() => req(80)}>{course.session_80_unlocked ? "Book second session (80%)" : "Reach 80% to book"}</Btn>
      </div>
      <div className="space-y-2">
        {(course.live_sessions || []).length === 0 && <div className="text-sm text-slate-400">No scheduled live sessions yet.</div>}
        {(course.live_sessions || []).map((s, i) => (
          <Card key={i} className="p-4 flex items-center gap-3"><span className="text-slate-500"><Icon name="calendar" size={16} /></span><div><div className="text-sm font-medium text-slate-800">{s.date}{s.time ? ` · ${s.time}` : ""}</div>{s.link && <a href={s.link} className="text-xs" style={{ color: BRAND }}>Join link</a>}</div></Card>
        ))}
      </div>
    </div>
  );
}

/* left course menu with locks — modules are headings, sub-modules are lessons */
function Menu({ course, view, setView }) {
  const activeSubId = view.type === "lesson" ? view.subId : null;
  const moduleOfActive = activeSubId
    ? course.modules.find((m) => (m.sub_modules || []).some((sm) => sm.id === activeSubId))
    : null;
  const [openMods, setOpenMods] = useState(
    () => new Set([moduleOfActive ? moduleOfActive.id : (course.modules[0] && course.modules[0].id)].filter(Boolean))
  );
  // keep the active lesson's module open
  useEffect(() => {
    if (moduleOfActive) setOpenMods((s) => new Set(s).add(moduleOfActive.id));
  }, [moduleOfActive && moduleOfActive.id]);
  const toggleMod = (id) => setOpenMods((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const item = (label, active, locked, onClick) => (
    <button key={label} disabled={locked} onClick={onClick}
      className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-left mb-1 ${active ? "text-white" : locked ? "text-slate-400" : "text-slate-700 hover:bg-slate-100"}`}
      style={active ? { backgroundColor: BRAND } : {}}>
      <span className="truncate">{label}</span>{locked && <Icon name="lock" size={11} className="shrink-0" />}
    </button>
  );
  const done = course.progress >= 100;
  return (
    <Card className="p-4 w-64 shrink-0 h-fit max-h-[80vh] overflow-y-auto">
      <div className="text-sm font-semibold text-slate-500 mb-3">Course menu</div>
      {item("Introduction", view.type === "intro", false, () => setView({ type: "intro" }))}
      {course.modules.map((m, i) => {
        const open = openMods.has(m.id);
        const subs = m.sub_modules || [];
        const moduleLocked = subs.length > 0 && subs.every((sm) => !sm.unlocked);
        return (
          <div key={m.id} className="mt-1">
            <button onClick={() => toggleMod(m.id)}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-100 text-left">
              <Icon name={open ? "chevron-down" : "chevron-right"} size={14} className="text-slate-400 shrink-0" />
              <span className="flex-1 font-bold text-slate-800 text-[13px] truncate">Module {i + 1}: {m.title}</span>
              {moduleLocked && <Icon name="lock" size={11} className="text-slate-400 shrink-0" />}
            </button>
            {open && (
              <div className="ml-3 pl-2 border-l-2 border-slate-100 mt-1">
                {subs.map((sm) => item(sm.title || "Untitled lesson", view.type === "lesson" && view.subId === sm.id, !sm.unlocked, () => setView({ type: "lesson", subId: sm.id })))}
                {subs.length === 0 && <div className="text-[11px] text-slate-300 px-3 py-1">No lessons yet</div>}
              </div>
            )}
          </div>
        );
      })}
      <div className="mt-3 pt-2 border-t border-slate-100">
        {item("Course feedback", view.type === "feedback", false, () => setView({ type: "feedback" }))}
        {course.final_assessment_enabled !== false && item("Final assessment", view.type === "final", !done, () => setView({ type: "final" }))}
        {item("Certificate", view.type === "certificate", !course.certificate_available, () => setView({ type: "certificate" }))}
      </div>
    </Card>
  );
}

export default function Course({ toast, sidebar }) {
  const [course, setCourse] = useState(null);
  const [view, setView] = useSubView({ type: "intro" });
  const load = () => { const cid = localStorage.getItem("activeCourseId"); return (cid ? api.myCourseById(Number(cid)).catch(() => { localStorage.removeItem("activeCourseId"); return api.myCourse(); }) : api.myCourse()).then(setCourse).catch((e) => toast && toast(e.message)); };
  useEffect(() => { load(); }, []);
  // Collapse the app sidebar when the student is inside a lesson, to give the
  // course content room; expand again when they leave the lesson view.
  useEffect(() => {
    if (sidebar && sidebar.setCollapsed) sidebar.setCollapsed(view.type === "lesson");
  }, [view.type]);
  if (!course) return <div className="text-slate-400 text-sm">Loading…</div>;

  // flatten sub-modules across modules in learning order
  const flatSubs = [];
  course.modules.forEach((m) => (m.sub_modules || []).forEach((sm) => flatSubs.push(sm)));
  const sub = view.subId ? flatSubs.find((sm) => sm.id === view.subId) : null;
  const nextSubOf = (id) => { const i = flatSubs.findIndex((sm) => sm.id === id); return i >= 0 ? flatSubs[i + 1] || null : null; };
  const refresh = () => load();

  return (
    <div className="flex gap-6 items-start">
      <Menu course={course} view={view} setView={setView} />
      <div className="flex-1 min-w-0">
        {view.type === "intro" && <Intro course={course} />}
        {view.type === "lesson" && sub && (() => {
          const next = nextSubOf(sub.id);
          return <Lesson course={course} sub={sub} onChanged={refresh} go={setView}
            hasNext={!!next}
            onNext={() => { refresh(); setView(next ? { type: "lesson", subId: next.id } : (course.final_assessment_enabled !== false ? { type: "final" } : { type: "certificate" })); }} />;
        })()}
        {view.type === "quiz" && sub && (() => {
          const next = nextSubOf(sub.id);
          return <Quiz subId={sub.id} sub={sub} toast={toast}
            hasNext={!!next}
            onNext={() => { refresh(); setView(next ? { type: "lesson", subId: next.id } : (course.final_assessment_enabled !== false ? { type: "final" } : { type: "certificate" })); }}
            onChanged={() => { setView({ type: "lesson", subId: sub.id }); refresh(); }} />;
        })()}
        {view.type === "feedback" && <Feedback course={course} onChanged={refresh} toast={toast} />}
        {view.type === "final" && <Final course={course} onChanged={refresh} toast={toast} />}
        {view.type === "certificate" && <Certificate course={course} onChanged={refresh} toast={toast} />}
      </div>
    </div>
  );
}
